import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { parse } from "yaml";

import UiButton from "../button/Button";
import VariableHighlightEditor from "../variable-editor/VariableHighlightEditor";
import ConditionGroupEditor from "./ConditionGroupEditor";
import { FieldOption } from "./ValueEditor";
import {
	BuilderNode,
	ConditionGroup,
	builderModelToText,
	emptyGroup,
	isGroup,
	newConditionId,
	yamlNodeToBuilderModel,
} from "../../utils/conditions";

import "./conditions-builder.css";

interface ConditionsBuilderProps {
	value: BuilderNode | null;
	onChange: (value: BuilderNode | null) => void;
	/** Raw YAML backing the advanced editor (kept in sync by the parent). */
	rawYaml: string;
	onRawYamlChange: (yaml: string) => void;
	isAdvanced: boolean;
	onToggleAdvanced: (next: boolean) => void;
	/** Fields offered in the rule dropdowns (participant user_state columns). */
	fields: FieldOption[];
	/** Names highlighted as known in the advanced YAML editor. */
	knownVariables: string[];
}

/** State of the current raw YAML relative to the visual builder. */
type YamlStatus =
	| { kind: "ok" }
	| { kind: "empty" }
	| { kind: "invalid"; message: string }
	| { kind: "unrepresentable" };

const inspectYaml = (rawYaml: string): YamlStatus => {
	if (!rawYaml.trim()) return { kind: "empty" };
	let parsed: unknown;
	try {
		parsed = parse(rawYaml);
	} catch (error) {
		return { kind: "invalid", message: (error as Error).message };
	}
	if (parsed === null || parsed === undefined) return { kind: "empty" };
	return yamlNodeToBuilderModel(parsed) ? { kind: "ok" } : { kind: "unrepresentable" };
};

/**
 * No-code conditions editor. Presents a recursive AND/OR builder that emits the
 * campaign conditions DSL, with an "Advanced (YAML)" escape hatch for anything
 * the builder can't express. The builder and YAML views are reconciled on every
 * mode switch so neither silently discards the user's work.
 */
const ConditionsBuilder = ({
	value,
	onChange,
	rawYaml,
	onRawYamlChange,
	isAdvanced,
	onToggleAdvanced,
	fields,
	knownVariables,
}: ConditionsBuilderProps) => {
	const { t } = useTranslation("common");

	// The builder always edits a root group; a lone rule is wrapped for display.
	const rootGroup: ConditionGroup = useMemo(() => {
		if (value === null) return emptyGroup("and");
		if (isGroup(value)) return value;
		return { id: newConditionId(), kind: "and", children: [value] };
	}, [value]);

	const handleRootChange = (group: ConditionGroup) =>
		onChange(group.children.length === 0 ? null : group);

	const switchToAdvanced = () => {
		// Seed the YAML from the current model so nothing is lost on the way in.
		onRawYamlChange(builderModelToText(value));
		onToggleAdvanced(true);
	};

	const switchToBuilder = () => {
		const status = inspectYaml(rawYaml);
		if (status.kind === "empty") {
			onChange(null);
			onToggleAdvanced(false);
			return;
		}
		if (status.kind === "ok") {
			onChange(yamlNodeToBuilderModel(parse(rawYaml)));
			onToggleAdvanced(false);
			return;
		}
		// Invalid or un-representable: stay in YAML; the notice explains why.
	};

	if (isAdvanced) {
		const status = inspectYaml(rawYaml);
		const canReturn = status.kind === "ok" || status.kind === "empty";
		return (
			<div className="cb-root cb-root--advanced">
				<div className="cb-toolbar">
					<span className="cb-mode-label">
						{t("MARKETING_CAMPAIGNS.CONDITIONS.ADVANCED_MODE")}
					</span>
					<UiButton
						type="button"
						className="p-button-text p-button-sm"
						icon="pi pi-th-large"
						label="MARKETING_CAMPAIGNS.CONDITIONS.BACK_TO_BUILDER"
						onClick={switchToBuilder}
						disabled={!canReturn}
					/>
				</div>
				<VariableHighlightEditor
					value={rawYaml}
					onChange={onRawYamlChange}
					knownVariables={knownVariables}
					minHeight={140}
					placeholder={t("MARKETING_CAMPAIGNS.FIELDS.CONDITIONS_PLACEHOLDER")}
				/>
				{status.kind === "invalid" && (
					<small className="cb-notice cb-notice--error">
						{t("MARKETING_CAMPAIGNS.CONDITIONS.YAML_INVALID")}: {status.message}
					</small>
				)}
				{status.kind === "unrepresentable" && (
					<small className="cb-notice cb-notice--warn">
						{t("MARKETING_CAMPAIGNS.CONDITIONS.YAML_UNREPRESENTABLE")}
					</small>
				)}
			</div>
		);
	}

	return (
		<div className="cb-root">
			<div className="cb-toolbar">
				<span className="cb-mode-label">
					{t("MARKETING_CAMPAIGNS.CONDITIONS.BUILDER_MODE")}
				</span>
				<UiButton
					type="button"
					className="p-button-text p-button-sm"
					icon="pi pi-code"
					label="MARKETING_CAMPAIGNS.CONDITIONS.ADVANCED_YAML"
					onClick={switchToAdvanced}
				/>
			</div>
			<ConditionGroupEditor
				group={rootGroup}
				onChange={handleRootChange}
				fields={fields}
				depth={0}
			/>
		</div>
	);
};

export default ConditionsBuilder;
